/* eslint-disable @typescript-eslint/no-explicit-any */

import { useContent } from '@oneaudi/feature-app-utils';
import * as React from 'react';
import { UeContainer } from '@oneaudi/falcon-tools';
import { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import { OneGraphServiceV1 } from '@oneaudi/onegraph-service';
import type { Logger } from '@feature-hub/logger';
import { ThemeProvider } from '@oneaudi/unified-web-common';
import { EditorJsonContent, FalconContent, mapContent } from '../contentMapping/contentMapping';
import { APP_ID } from '../../environment';
import type {
  EditorialTeaserComponent,
  EditorialTeaserContent,
  EditorialTeaserServices,
} from '../../types';
import { TrackingClickEvent, useTracking } from '../tracking';
import { isDebugMode } from '../utils';
import { Teaser } from './EditorialTeaser';
import { TeaserContainer } from './EditorialTeaserContainer';
import { getThemeName } from '../../alpha-helpers';

export const EditorialTeasers = ({
  components,
  themeColor = 'light',
  localeService,
  trackingService,
  chunkedComponentsSSR,
  oneGraphService,
  loggerService,
}: EditorialTeaserContent &
  EditorialTeaserServices & {
    chunkedComponentsSSR: any;
  }): React.ReactElement => {
  const chunkedComponents: any = chunkedComponentsSSR || sliceComponentsIntoChunks(components, 3);
  const ref = React.useRef(null);
  let relatedElements: any;
  if (typeof window !== 'undefined') {
    relatedElements = React.useRef(
      components.map((x, i) => ({
        elementName: 'teaser',
        elementValue: `${i} - ${
          x.heading || `${x.text ? `${x.text?.slice(0, 10)}...` : '(no text)'}`
        }`,
      })),
    );
  }
  const {
    registerImpressionTracking,
    sendReadyEvent,
    sendClickEvent: _sendClickEvent,
  } = useTracking(trackingService);
  const sendClickEvent = React.useCallback(
    (event: TrackingClickEvent, componentIndex: number) => {
      _sendClickEvent({
        ...event,
        relatedElements: [relatedElements.current[componentIndex]],
      });
    },
    [_sendClickEvent],
  );

  React.useEffect(() => sendReadyEvent(), []);

  React.useEffect(() => {
    if (ref.current)
      registerImpressionTracking(ref.current, {
        relatedElements: relatedElements.current,
      });
  }, [ref]);

  return (
    <ThemeProvider theme={{ name: getThemeName(themeColor) }}>
      <div ref={ref} data-testid="editorial-teaser">
        <UeContainer propertyPath="components">
          {chunkedComponents?.map((chunk: ChunkedEditorialTeaserComponent[]) => {
            const containerKey = chunk.map((c) => c.originalIndex).join('-');
            return (
              <TeaserContainer
                key={containerKey}
                numberOfComponents={chunk.length}
                data-testid="teaserContainer"
              >
                {chunk?.map((chunkedComponent: ChunkedEditorialTeaserComponent, i: number) => {
                  const teaserKey = [
                    chunkedComponent.originalIndex,
                    chunkedComponent.heading?.replace(/\s+/g, ''),
                  ].join('_');
                  return (
                    <Teaser
                      key={teaserKey}
                      index={i}
                      content={chunkedComponent}
                      numberOfComponents={chunk.length}
                      sendClickEvent={sendClickEvent}
                      localeService={localeService}
                      oneGraphService={oneGraphService}
                      loggerService={loggerService}
                    />
                  );
                })}
              </TeaserContainer>
            );
          })}
        </UeContainer>
      </div>
    </ThemeProvider>
  );
};

export const EditorialTeaserContentLoader = ({
  initialContent,
  localeService,
  loggerService,
  oneGraphService,
  ssrContent,
  chunkedComponents,
  trackingService,
}: {
  initialContent?: EditorialTeaserContent;
  ssrContent: EditorialTeaserContent;
  chunkedComponents: ChunkedEditorialTeaserComponent[];
  trackingService?: TrackingServiceV2;
  loggerService?: Logger;
  oneGraphService: OneGraphServiceV1;
} & EditorialTeaserServices): React.ReactElement | null => {
  let content: EditorJsonContent;
  if (!ssrContent) {
    const rawContent = useContent<EditorJsonContent | FalconContent>() || initialContent;
    // eslint-disable-next-line no-console
    content = mapContent(rawContent as EditorJsonContent | FalconContent);
  } else {
    content = ssrContent;
  }

  if (isDebugMode())
    // eslint-disable-next-line no-console
    console.debug(`${APP_ID} ->`, {
      content,
    });

  if (!content) return null;

  return (
    <EditorialTeasers
      {...content}
      localeService={localeService}
      trackingService={trackingService}
      chunkedComponentsSSR={chunkedComponents}
      loggerService={loggerService}
      oneGraphService={oneGraphService}
    />
  );
};

interface ChunkedEditorialTeaserComponent extends EditorialTeaserComponent {
  // additional prop for remembering the index from the original
  // array of components before chunking it. Only used for
  // tracking purposes.
  originalIndex: number;
}

/**
 * Put teaser components data in a chunked array so that
 * more than 3 columns can be rendered
 */
function sliceComponentsIntoChunks(
  components: EditorialTeaserComponent[],
  chunkSize: number,
): ChunkedEditorialTeaserComponent[][] {
  const chunkedComponents: ChunkedEditorialTeaserComponent[][] = [];
  let chunk: ChunkedEditorialTeaserComponent[] = [];

  if (typeof window !== 'undefined') {
    components.forEach((component, componentIndex) => {
      chunk.push({ ...component, originalIndex: componentIndex });

      if (chunk.length >= chunkSize) {
        chunkedComponents.push(chunk);
        chunk = [];
      }
    });

    if (chunk.length) {
      chunkedComponents.push(chunk);
    }
  }

  return chunkedComponents;
}
